import { Button, ClickAwayListener, Link, Tooltip, Typography } from '@material-ui/core';
import { lighten } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

const NEW_WEB_URL = 'https://v2.flyid.panoramaid.com.br';
const SUPPORT_URL = 'https://panoramaid.atlassian.net/servicedesk/customer/portals';
// Local midnight of November 1st, when the notice switches to warning colors
const WARNING_FROM = new Date(2026, 10, 1);
const APPS = [
  { name: 'Fly.id (Air 2S, Mavic 2)', url: 'https://play.google.com/store/apps/details?id=com.panoramaid.flyid.v2' },
  {
    name: 'Fly.id Pilot (Mini 4 Pro)',
    url: 'https://play.google.com/store/apps/details?id=com.panoramaid.flyid.v2.pilot',
  },
  { name: 'Count.id', url: 'https://play.google.com/store/apps/details?id=com.panoramaid.countid.v2' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 1),
    padding: theme.spacing(0.75, 2),
    backgroundColor: lighten(theme.palette.primary.light, 0.85),
    color: theme.palette.primary.dark,
    textAlign: 'center',
  },
  warning: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  icon: {
    fontSize: '1.2rem',
  },
  learnMore: {
    fontWeight: 'bold',
    textTransform: 'none',
  },
  tooltip: {
    maxWidth: 340,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[6],
    fontSize: theme.typography.body2.fontSize,
  },
  arrow: {
    color: theme.palette.common.white,
  },
  paragraph: {
    marginBottom: theme.spacing(1.5),
  },
  linkList: {
    margin: theme.spacing(0.5, 0, 1.5),
    paddingLeft: theme.spacing(2.5),
  },
}));

const MigrationNotice = () => {
  const classes = useStyles();
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const externalLink = (href, label) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" color="primary">
      {label}
    </Link>
  );

  const details = (
    <>
      <Typography variant="subtitle2" gutterBottom>
        {intl.formatMessage({ id: 'migration.title' })}
      </Typography>
      <Typography variant="body2" className={classes.paragraph}>
        {intl.formatMessage({ id: 'migration.ready' })}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="small"
        href={NEW_WEB_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.paragraph}
      >
        {intl.formatMessage({ id: 'migration.openWeb' })}
      </Button>
      <Typography variant="body2">{intl.formatMessage({ id: 'migration.apps' })}</Typography>
      <ul className={classes.linkList}>
        {APPS.map(({ name, url }) => (
          <li key={name}>{externalLink(url, name)}</li>
        ))}
      </ul>
      <Typography variant="body2">
        {externalLink(SUPPORT_URL, intl.formatMessage({ id: 'migration.support' }))}
      </Typography>
    </>
  );

  return (
    <div className={clsx(classes.root, new Date() >= WARNING_FROM && classes.warning)}>
      <InfoOutlinedIcon className={classes.icon} />
      <Typography variant="body2">{intl.formatMessage({ id: 'migration.banner' })}</Typography>
      {/* Opens on hover and on click, so the links stay reachable on touch screens */}
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <span>
          <Tooltip
            interactive
            arrow
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            title={details}
            classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
          >
            <Button size="small" color="inherit" className={classes.learnMore} onClick={() => setOpen(true)}>
              {intl.formatMessage({ id: 'migration.learnMore' })}
            </Button>
          </Tooltip>
        </span>
      </ClickAwayListener>
    </div>
  );
};

export default MigrationNotice;
